import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import heroImage from '../images/thumbnail_ES_210916-615 liggande värdshus.jpg';
import teamImage from '../images/2025-11-18JytteLindberg(73) (1).jpg';

const Home: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative" style={{ height: '70vh', minHeight: '500px' }}>
        <img src={heroImage} alt="Gustavsbergs Värdshus" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: 'center 80%' }} />
        <div className="absolute inset-0 bg-black opacity-40" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-6xl md:text-8xl font-bold text-white mb-6" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            {t('hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-white mb-12 max-w-2xl" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
            {t('hero.subtitle')}
          </p>
          <a href="https://www.bokadirekt.se/places/varmdo-horsel-134414" target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-[#0A4A7A] px-10 py-4 rounded text-lg font-semibold hover:bg-gray-100 transition-colors" style={{ marginTop: '70px' }}>
            {t('hero.cta')}
          </a>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0A4A7A] mb-12">{t('home.servicesTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              { key: 'horseltest', icon: 'M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3', link: '/horseltest' },
              { key: 'horapparater', icon: 'ear', link: '/horapparater' },
              { key: 'service', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', link: '/service' },
              { key: 'yrkesliv', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', link: '/arbetshjalpmedel' },
              { key: 'horselskydd', icon: 'M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z', link: '/horselskydd' }
            ].map(({ key, icon, link }) => (
              <div key={key} className="text-center p-6 bg-gray-50 rounded-lg flex flex-col">
                <div className="w-16 h-16 bg-[#0A4A7A] rounded-full flex items-center justify-center mx-auto mb-4">
                  {icon === 'ear' ? (
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 32 32">
                      <path d="M16.97 1.25c-4.86 0-9.78 3.692-9.78 10.75 0 0.414 0.336 0.75 0.75 0.75s0.75-0.336 0.75-0.75v0c0-6.355 4.292-9.25 8.28-9.25s8.28 2.895 8.28 9.25c0.005 0.122 0.007 0.265 0.007 0.409 0 0.756-0.071 1.496-0.206 2.213l0.012-0.074c-0.373 1.716-1.119 3.218-2.151 4.476l0.015-0.018c-0.197 0.238-0.416 0.455-0.631 0.67-0.357 0.338-0.68 0.704-0.97 1.097l-0.016 0.023c-0.415 0.612-0.823 1.32-1.18 2.057l-0.045 0.103c-0.256 0.47-0.499 1.025-0.696 1.601l-0.023 0.078c-0.090 0.299-0.153 0.648-0.177 1.007l-0.001 0.015c-0.016 0.257-0.056 0.496-0.119 0.726l0.006-0.025c-0.091 0.311-0.195 0.576-0.318 0.829l0.014-0.031c-0.252 0.485-0.574 0.897-0.96 1.24l-0.004 0.004c-0.805 0.687-1.821 1.149-2.938 1.279l-0.026 0.002c-0.25 0.034-0.538 0.054-0.831 0.054-0.894 0-1.745-0.181-2.52-0.509l0.042 0.016c-1.001-0.418-1.802-1.151-2.296-2.073l-0.012-0.024c-0.3-0.609-0.476-1.326-0.476-2.084 0-0.021 0-0.043 0-0.064l-0 0.003c0-0.414-0.336-0.75-0.75-0.75s-0.75 0.336-0.75 0.75v0c-0 0.017-0 0.038-0 0.058 0 1.004 0.234 1.953 0.652 2.795l-0.016-0.037c0.667 1.262 1.727 2.242 3.015 2.79l0.040 0.015c0.889 0.389 1.926 0.615 3.015 0.615 0.011 0 0.022-0 0.032-0h-0.002c0.001 0 0.003 0 0.004 0 0.368 0 0.73-0.024 1.086-0.071l-0.042 0.005c1.457-0.168 2.75-0.763 3.778-1.656l-0.009 0.007c0.528-0.47 0.965-1.029 1.29-1.656l0.015-0.032c0.144-0.288 0.28-0.629 0.386-0.983l0.012-0.047c0.088-0.294 0.15-0.636 0.173-0.988l0.001-0.014c0.017-0.264 0.058-0.51 0.123-0.747l-0.006 0.026c0.195-0.569 0.408-1.051 0.655-1.51l-0.026 0.053c0.365-0.767 0.736-1.413 1.149-2.029l-0.036 0.056c0.253-0.339 0.521-0.638 0.813-0.913l0.004-0.003c0.25-0.25 0.5-0.5 0.729-0.777 1.165-1.418 2.019-3.136 2.431-5.019l0.015-0.079c0.145-0.729 0.228-1.567 0.228-2.425 0-0.153-0.003-0.305-0.008-0.457l0.001 0.022c0-7.058-4.92-10.75-9.78-10.75z"/>
                    </svg>
                  ) : (
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                      {key === 'service' && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />}
                    </svg>
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-[#0A4A7A]">{t(`home.${key}`)}</h3>
                <div className="flex-grow"></div>
                <Link to={link} className="inline-block bg-[#0A4A7A] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#083d66] transition-colors mt-4">
                  {t('home.moreInfo')}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Drop-in Section */}
      <section className="py-16 bg-[#0A4A7A]">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">{t('home.dropinTitle')}</h2>
          <p className="text-xl text-gray-200 mb-2">{t('home.dropinSubtitle')}</p>
          <p className="text-lg text-gray-200">{t('home.dropinHours')}</p>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img src={teamImage} alt="Värmdö Hörsel team" className="w-full object-cover rounded-lg h-64 md:h-[500px]" style={{ objectPosition: 'center 20%' }} />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-[#0A4A7A] mb-6">{t('home.teamTitle')}</h2>
              <p className="text-gray-700 mb-4">{t('home.teamText1')}</p>
              <p className="text-gray-700 mb-4">{t('home.teamText2')}</p>
              <p className="text-gray-700 mb-4">{t('home.teamText3')}</p>
              <p className="text-gray-700 font-semibold">{t('home.teamSignature')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-[#0A4A7A] py-16">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white mb-4">{t('services.bookTitle')}</h2>
          <p className="text-xl text-gray-200 mb-8">{t('services.bookDesc')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://www.bokadirekt.se/places/varmdo-horsel-134414" target="_blank" rel="noopener noreferrer" className="bg-white text-[#0A4A7A] px-8 py-3 rounded font-semibold hover:bg-gray-100 transition-colors">
              {t('hero.cta')}
            </a>
            <a href="tel:072-334 47 74" className="border border-white text-white px-8 py-3 rounded font-semibold hover:bg-white/10 transition-colors">
              {t('services.call')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
